import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { Upload, Camera, Sparkles, Download, Share2 } from "lucide-react";
import { toast } from "sonner";
import { resolveMediaUrl, downloadAssetUrl, API_BASE } from "@/lib/api";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function CustomerVisualizer() {
  const { slug } = useParams();
  const [retailer, setRetailer] = useState(null);
  const [storeError, setStoreError] = useState("");
  const [step, setStep] = useState("upload"); // upload | waiting | done | failed
  const [sessionId, setSessionId] = useState(null);
  const [outputUrls, setOutputUrls] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [customerName, setCustomerName] = useState("");
  const [customerPhone, setCustomerPhone] = useState("");
  const cameraRef = useRef(null);

  useEffect(() => {
    api.get(`/public/retailer/${slug}`)
      .then((r) => setRetailer(r.data))
      .catch(() => setStoreError("The QR link may be outdated. Ask the retailer for an updated code."));
  }, [slug]);

  const handleFile = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data: uploaded } = await api.post("/uploads/public", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const { data: session } = await api.post(`/public/retailer/${slug}/session`, {
        room_image_url: uploaded.url,
        customer_name: customerName.trim(),
        customer_phone: customerPhone.trim(),
      });
      setSessionId(session.id);
      setStep("waiting");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const pollSession = useCallback((id) => {
    let done = false;
    let ws = null;
    let fallback = null;

    const finish = (status, urls, error) => {
      if (done) return;
      done = true;
      clearInterval(fallback);
      try { ws?.close(); } catch (_) {}
      if (status === "completed") {
        setOutputUrls(urls || []);
        setStep("done");
      } else {
        setStep("failed");
        toast.error(error || "Generation failed");
      }
    };

    const onMsg = (data) => {
      if (data.type === "ping") return;
      if (data.status === "completed") finish("completed", data.output_urls);
      else if (data.status === "failed") finish("failed", [], data.error);
    };

    try {
      const wsBase = BACKEND_URL.replace(/^http/, "ws");
      ws = new WebSocket(`${wsBase}/api/ws/session/${id}`);
      ws.onmessage = (e) => { try { onMsg(JSON.parse(e.data)); } catch (_) {} };
      ws.onerror = () => startFallback();
      ws.onclose = () => { if (!done) startFallback(); };
    } catch (_) {
      startFallback();
    }

    function startFallback() {
      if (fallback || done) return;
      fallback = setInterval(async () => {
        if (done) return;
        try {
          const { data } = await api.get(`/public/session/${id}`);
          if (data.status === "completed") finish("completed", data.output_urls);
          else if (data.status === "failed") finish("failed", [], data.error);
        } catch (_) {}
      }, 4000);
    }
  }, []);

  useEffect(() => {
    if (sessionId && step === "waiting") pollSession(sessionId);
  }, [sessionId, step, pollSession]);

  if (storeError) {
    return (
      <div className="min-h-[100dvh] flex items-center justify-center bg-[#FAFAFA] p-8">
        <div className="text-center">
          <Logo />
          <h2 className="mt-8 text-2xl font-display font-medium">Store not found</h2>
          <p className="mt-2 text-[#5C5F62]">{storeError}</p>
        </div>
      </div>
    );
  }

  if (step === "waiting") {
    return (
      <div className="min-h-[100dvh] bg-[#0F1011] text-white flex flex-col items-center justify-center p-8 text-center">
        <Logo />
        <div className="mt-10 h-16 w-16 rounded-full border-4 border-[#C86B53] border-t-transparent animate-spin" />
        <h2 className="mt-8 text-2xl font-display font-medium">Your room is in the queue</h2>
        <p className="mt-2 text-white/60 max-w-sm">
          A salesperson will pick up your image and generate the render. This usually takes a few minutes.
        </p>
        <p className="mt-6 text-xs text-white/30">Keep this page open to see your result automatically.</p>
      </div>
    );
  }

  if (step === "done") {
    return (
      <div className="min-h-[100dvh] bg-[#0F1011] text-white flex flex-col items-center justify-center p-8 text-center">
        <Logo />
        <h2 className="mt-8 text-2xl font-display font-medium">Your render is ready! 🎉</h2>
        <div className="mt-6 grid grid-cols-1 gap-4 w-full max-w-lg">
          {outputUrls.map((url, i) => (
            <img key={i} src={resolveMediaUrl(url)} alt="render" className="w-full rounded-xl object-cover" />
          ))}
        </div>
        <div className="mt-6 flex gap-3 flex-wrap justify-center">
          {outputUrls[0] && (
            <a
              href={downloadAssetUrl(outputUrls[0])}
              download
              className="neivai-btn-primary inline-flex items-center gap-2"
            >
              <Download className="h-4 w-4" /> Download
            </a>
          )}
          <button
            onClick={() => { setStep("upload"); setSessionId(null); setOutputUrls([]); }}
            className="neivai-btn-secondary inline-flex items-center gap-2"
          >
            <Sparkles className="h-4 w-4 text-white" /> Try another room
          </button>
        </div>
      </div>
    );
  }

  if (step === "failed") {
    return (
      <div className="min-h-[100dvh] bg-[#0F1011] text-white flex flex-col items-center justify-center p-8 text-center">
        <Logo />
        <h2 className="mt-8 text-2xl font-display font-medium">Something went wrong</h2>
        <p className="mt-2 text-white/60">The render failed. Please try again.</p>
        <button
          onClick={() => { setStep("upload"); setSessionId(null); }}
          className="mt-6 neivai-btn-primary"
        >
          Try again
        </button>
      </div>
    );
  }

  // step === "upload"
  return (
    <div className="min-h-[100dvh] bg-[#0F1011] text-white flex flex-col">
      <header className="glass-panel !bg-white/10 !border-white/20 sticky top-0 z-30 px-4 py-3 flex items-center justify-between">
        <Logo variant="light" size="sm" />
        <p className="text-xs uppercase tracking-widest text-white/60">
          Experience by {retailer?.business_name || "…"}
        </p>
      </header>

      <main className="flex-1 flex items-center justify-center p-6">
        <div className="max-w-md w-full">
          <h1 className="text-4xl font-display font-semibold text-center">Your room + our furniture</h1>
          <p className="mt-3 text-white/60 text-center">
            Upload a photo of your room. A salesperson will place products and send you the render.
          </p>

          <div className="mt-6 space-y-3">
            <input
              type="text"
              placeholder="Your name (optional)"
              value={customerName}
              onChange={(e) => setCustomerName(e.target.value)}
              className="w-full bg-white/5 border border-white/10 rounded-full px-5 py-3 text-sm placeholder:text-white/40 focus:outline-none focus:ring-2 focus:ring-[#C86B53]/40"
            />
            <input
              type="tel"
              placeholder="Phone number (optional)"
              value={customerPhone}
              onChange={(e) => setCustomerPhone(e.target.value)}
              className="w-full bg-white/5 border border-white/10 rounded-full px-5 py-3 text-sm placeholder:text-white/40 focus:outline-none focus:ring-2 focus:ring-[#C86B53]/40"
            />
          </div>

          <div className="mt-6 space-y-3">
            <label className="block cursor-pointer border-2 border-dashed border-white/30 hover:border-[#C86B53] rounded-xl p-8 transition text-center">
              <input
                type="file"
                accept="image/*"
                className="hidden"
                disabled={uploading}
                onChange={(e) => handleFile(e.target.files?.[0])}
              />
              <Upload className="h-6 w-6 mx-auto mb-3 text-white/70" />
              <p className="text-sm">{uploading ? "Uploading…" : "Tap to upload room image"}</p>
            </label>
            <label className="block cursor-pointer border-2 border-dashed border-white/30 hover:border-[#C86B53] rounded-xl p-4 transition text-center">
              <input
                ref={cameraRef}
                type="file"
                accept="image/*"
                capture="environment"
                className="hidden"
                disabled={uploading}
                onChange={(e) => handleFile(e.target.files?.[0])}
              />
              <p className="text-sm flex items-center justify-center gap-2">
                <Camera className="h-4 w-4" /> Capture with camera
              </p>
            </label>
          </div>
        </div>
      </main>
    </div>
  );
}
